// Vistoria sem internet (pedido 2026-10-16: "o motorista conseguir fazer
// checklist etc. mesmo sem internet"). Toda vistoria é gravada PRIMEIRO no
// celular (IndexedDB, com as fotos já carimbadas) e só depois enviada —
// sem sinal ela fica "Aguardando envio" e sobe sozinha quando a internet
// volta (evento online, ao abrir o app e a cada minuto, ver
// useOfflineInspectionSync). O clientId gerado aqui faz o backend devolver
// a MESMA vistoria se o envio cair no meio e for repetido (nunca duplica);
// cada foto enviada sai do registro na hora, então a retomada continua de
// onde parou.
import { useEffect, useSyncExternalStore } from "react";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { mapWithConcurrency } from "@/lib/concurrency";
import {
  vehicleInspectionsService,
  type CreateInspectionInput,
  type VehicleInspection,
} from "@/services/gpsdrive-vehicle-inspections.service";

export interface PendingInspectionPhoto {
  id: string;
  label: string;
  blob: Blob;
  name: string;
}

export interface PendingInspection {
  // = clientId mandado ao backend.
  localId: string;
  userId: string;
  performedAt: string;
  vehicleName: string;
  plate: string | null;
  hasIssues: boolean;
  input: CreateInspectionInput;
  photos: PendingInspectionPhoto[];
  // Preenchido depois que a vistoria foi criada no servidor (faltam fotos).
  serverId: string | null;
  uploadedPhotos: number;
  // Recusada pelo servidor (ex.: veículo desvinculado) — não tenta mais
  // sozinha; o motorista vê o motivo e pode descartar.
  rejected: string | null;
}

const DB_NAME = "gpsdrive-offline";
const STORE = "inspections";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: "localId" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const t = db.transaction(STORE, mode);
      const req = run(t.objectStore(STORE));
      t.oncomplete = () => resolve(req.result);
      t.onerror = () => reject(t.error);
      t.onabort = () => reject(t.error);
    });
  } finally {
    db.close();
  }
}

// ---- estado observável (lista "Aguardando envio") -----------------------

let snapshot: PendingInspection[] = [];
const listeners = new Set<() => void>();
let syncing = false;

async function refresh() {
  try {
    snapshot = ((await tx("readonly", (s) => s.getAll())) as PendingInspection[]).sort((a, b) =>
      a.performedAt.localeCompare(b.performedAt),
    );
  } catch {
    snapshot = [];
  }
  listeners.forEach((l) => l());
}

export function usePendingInspections(userId: string | null | undefined): PendingInspection[] {
  const all = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => snapshot,
    () => snapshot,
  );
  return userId ? all.filter((p) => p.userId === userId) : [];
}

export async function savePendingInspection(p: PendingInspection) {
  await tx("readwrite", (s) => s.put(p));
  await refresh();
}

export async function discardPendingInspection(localId: string) {
  await tx("readwrite", (s) => s.delete(localId));
  await refresh();
}

// Sem resposta do servidor (sem sinal, timeout, 5xx, sessão a renovar) =
// tenta de novo depois. 4xx de verdade = recusada, não adianta repetir.
function isRetryable(err: unknown) {
  if (!(err instanceof GpsDriveApiError)) return true;
  return err.status === 0 || err.status === 401 || err.status === 408 || err.status === 429 || err.status >= 500;
}

export type SendResult =
  | { status: "sent"; inspection: VehicleInspection; failedPhotos: number }
  | { status: "queued" }
  | { status: "rejected"; message: string };

// Envia UMA vistoria da fila (criação + fotos), gravando o progresso.
export async function sendPendingInspection(localId: string): Promise<SendResult> {
  let p = (await tx("readonly", (s) => s.get(localId))) as PendingInspection | undefined;
  if (!p) return { status: "queued" };
  try {
    const created = await vehicleInspectionsService.create({
      ...p.input,
      clientId: p.localId,
      performedAt: p.performedAt,
    });
    if (!p.serverId) {
      p = { ...p, serverId: created.id };
      await savePendingInspection(p);
    }
    let failedPhotos = 0;
    const uploaded = [...created.photos];
    // 3 fotos subindo ao mesmo tempo (2026-10-23) — uma a uma demorava demais.
    let cur: PendingInspection = p;
    await mapWithConcurrency([...p.photos], 3, async (photo) => {
      try {
        const file = new File([photo.blob], photo.name, { type: photo.blob.type || "image/jpeg" });
        uploaded.push(await vehicleInspectionsService.uploadPhoto(created.id, file, photo.label));
        cur = { ...cur, photos: cur.photos.filter((x) => x.id !== photo.id), uploadedPhotos: cur.uploadedPhotos + 1 };
        await savePendingInspection(cur);
      } catch (err) {
        if (isRetryable(err)) throw err;
        // Foto recusada (ex.: limite de fotos) — descarta só ela.
        failedPhotos++;
        cur = { ...cur, photos: cur.photos.filter((x) => x.id !== photo.id) };
        await savePendingInspection(cur);
      }
    });
    p = cur;
    await discardPendingInspection(p.localId);
    return { status: "sent", inspection: { ...created, photos: uploaded }, failedPhotos };
  } catch (err) {
    if (isRetryable(err)) return { status: "queued" };
    const message = err instanceof Error ? err.message : "Recusada pelo servidor";
    await savePendingInspection({ ...p, rejected: message });
    return { status: "rejected", message };
  }
}

// Envia tudo o que está na fila deste usuário (menos as recusadas).
export async function syncPendingInspections(userId: string): Promise<number> {
  if (syncing) return 0;
  syncing = true;
  let sent = 0;
  try {
    await refresh();
    for (const p of snapshot.filter((x) => x.userId === userId && !x.rejected)) {
      const r = await sendPendingInspection(p.localId);
      if (r.status === "sent") sent++;
      // Sem sinal: nem tenta as próximas agora.
      if (r.status === "queued") break;
    }
  } finally {
    syncing = false;
  }
  return sent;
}

// Montado no app (MobileApp): tenta enviar ao abrir, quando a internet
// volta e a cada minuto enquanto houver pendência.
export function useOfflineInspectionSync(userId: string | null | undefined, onSent?: (count: number) => void) {
  useEffect(() => {
    if (!userId || typeof indexedDB === "undefined") return;
    let alive = true;
    const run = async () => {
      if (!alive || (typeof navigator !== "undefined" && navigator.onLine === false)) return;
      const n = await syncPendingInspections(userId).catch(() => 0);
      if (alive && n > 0) onSent?.(n);
    };
    void refresh().then(run);
    window.addEventListener("online", run);
    const timer = window.setInterval(() => {
      if (snapshot.some((p) => p.userId === userId && !p.rejected)) void run();
    }, 60_000);
    return () => {
      alive = false;
      window.removeEventListener("online", run);
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onSent só avisa
  }, [userId]);
}
